// Enhanced Header component with logo image and responsive navigation
import React, { useContext, useState, useEffect, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../contexts_main/ThemeContext';
import { FilterContext } from '../../contexts_main/FilterContext';
import { createCountrySlug } from '../../utils/seo';
import { MenuIcon, MoonIcon, MapIcon, SunIcon, XIcon } from '../common/ShellIcons';

const AtlasMapSelector = React.lazy(() => import('../entities/AtlasMapSelector'));

// MODIFICATION: Accept the isScrolled prop from the Layout component
function Header({ isScrolled }) {
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const { 
    filters, updateFilter,
    viewMode, toggleViewMode,
    selectEntity, resetFilters
  } = useContext(FilterContext);
  
  const [showAtlasMap, setShowAtlasMap] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Set up global entity selection handler
  useEffect(() => {
    window.handleEntitySelectFromSearch = (entity) => {
      if (process.env.NODE_ENV !== 'production') console.log('Header: Entity selected from search:', entity);
      handleEntitySelection(entity);
    };
    
    window.openGlobalAtlasMap = () => {
      setShowAtlasMap(true);
    };
    
    return () => {
      delete window.handleEntitySelectFromSearch;
      delete window.openGlobalAtlasMap;
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.header') && !event.target.closest('.mobile-menu-dropdown')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);
  
  const handleEntitySelection = (entity) => {
    let entityType;
    switch (entity.type) {
      case 'person':
        entityType = 'people';
        break;
      case 'organization':
        entityType = 'organizations';
        break;
      case 'country':
        entityType = 'countries';
        break;
      case 'sector':
        entityType = 'sectors';
        break;
      default:
        entityType = `${entity.type}s`;
    }
    
    const currentEntities = filters[entityType] || [];
    
    if (!currentEntities.some(e => e.id === entity.id)) {
      const updatedEntities = [...currentEntities, entity];
      updateFilter(entityType, updatedEntities);
    }
    
    if (selectEntity) {
      selectEntity(entity);
    } else if (window.handleEntitySelectFromMain) {
      window.handleEntitySelectFromMain(entity);
    }
  };

  // Handle home navigation with filter reset
  const handleHomeNavigation = (e) => {
    // Always scroll to top, even if already on home page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Only reset filters if we're not already on the home page
    if (location.pathname !== '/') {
      resetFilters();
      navigate('/');
    }
    
    // Close mobile menu if open
    closeMobileMenu();
  };
  
  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  
  const handleMobileAtlasClick = () => {
    setShowAtlasMap(true);
    setIsMobileMenuOpen(false);
  };

  // Function to determine if a nav link is active
  const isActiveLink = (path) => {
    if (path === '/' && location.pathname === '/') {
      return true;
    }
    if (path !== '/' && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };
  
  return (
    <>
      {/* MODIFICATION: Apply the 'scrolled' class based on the prop */}
      <header className={`header extended-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-banner theme-banner"></div>
        <div className="header-content extended-layout">
          
          {/* Mobile header layout */}
          <div className="mobile-header-layout">
            {/* Mobile header row 1 - Logo and controls */}
            <div className="mobile-header-row-1">
              <Link to="/" className="mobile-logo" title="Go to Entities" aria-label="Trade Relations Atlas home" onClick={handleHomeNavigation}>
                <img 
                  src="/logo48.png"
                  width="48"
                  height="48"
                  alt="Trade Relations Atlas Logo" 
                />
              </Link>
              
              <div className="mobile-controls">
                <button 
                  className={`mobile-theme-toggle ${darkMode ? 'dark-mode-active' : 'light-mode-active'}`}
                  onClick={toggleTheme}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? <SunIcon /> : <MoonIcon />}
                </button>
                
                <button 
                  className="mobile-atlas-button"
                  onClick={handleMobileAtlasClick}
                  title="Select countries from World Map"
                  aria-label="Select countries from world map"
                >
                  <MapIcon />
                </button>
                
                <button 
                  className="mobile-menu-toggle"
                  onClick={toggleMobileMenu}
                  title="Toggle navigation menu"
                  aria-label="Toggle navigation menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
                </button>
              </div>
            </div>

            {/* Mobile header row 2 - Title and email (hidden when scrolled) */}
            <div className="mobile-header-row-2">
              <div className="mobile-title">
                <Link to="/" onClick={handleHomeNavigation} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <h1>Trade Relations Atlas</h1>
                </Link>
                <span className="mobile-subtitle">Exploring global trade relations</span>
              </div>
              <a 
                href="mailto:admin@traderelationsatlas.com"
                className="mobile-email-link"
                title="Contact us"
              >
                admin@traderelationsatlas.com
              </a>
            </div>
          </div>

          {/* Desktop header layout */}
          <div className="desktop-header-layout">
            <div className="header-left">
              <Link to="/" className="logo" onClick={handleHomeNavigation}>
                <h1>Trade Relations Atlas</h1>
                <span className="subtitle">Organizing news and data to map how global actors interact</span>
              </Link>
            </div>
            
            {/* Logo image section - make clickable */}
            <div className="header-logo">
              <Link to="/" title="Go to Entities" onClick={handleHomeNavigation}>
                <img 
                  src="/logo48.png"
                  width="48"
                  height="48"
                  alt="Trade Relations Atlas Logo" 
                />
              </Link>
            </div>
            
            {/* Navigation - responsive */}
            <nav className={`main-navigation ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
              <Link
                to="/"
                className={`nav-link ${isActiveLink('/') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Entities
              </Link>
              <Link
                to="/trade"
                className={`nav-link ${isActiveLink('/trade') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Trade Data
              </Link>
              <Link
                to="/sanctions"
                className={`nav-link ${isActiveLink('/sanctions') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Sanctions
              </Link>
              <Link
                to="/feeds"
                className={`nav-link ${isActiveLink('/feeds') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Feeds
              </Link>
              <Link
                to="/news"
                className={`nav-link ${isActiveLink('/news') ? 'active' : ''}`} 
                onClick={closeMobileMenu}
              >
                Articles
              </Link>
              <Link 
                to="/podcasts" 
                className={`nav-link ${isActiveLink('/podcasts') ? 'active' : ''}`} 
                onClick={closeMobileMenu}
              >
                Podcasts
              </Link>
              <Link 
                to="/streams" 
                className={`nav-link ${isActiveLink('/streams') ? 'active' : ''}`} 
                onClick={closeMobileMenu}
              >
                Streams
              </Link>
            </nav>
            
            <div className="header-center extended-center">
              <div className="header-buttons-row">
                <button 
                  className={`header-theme-toggle ${darkMode ? 'dark-mode-active' : 'light-mode-active'}`}
                  onClick={toggleTheme}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? <SunIcon /> : <MoonIcon />}
                  <span className="theme-label">{darkMode ? 'Light' : 'Dark'}</span>
                </button>
                
                <button 
                  className="world-map-button"
                  onClick={() => setShowAtlasMap(true)}
                  title="Select countries from World Map"
                >
                  <MapIcon />
                  <span className="button-label">World Map</span>
                </button>
              </div>
              
              {/* Email link underneath buttons */}
              <a 
                href="mailto:admin@traderelationsatlas.com"
                className="header-email-link"
                title="Contact us"
              >
                admin@traderelationsatlas.com
              </a>
            </div>
            
            <div className="header-right" />
          </div>
        </div>
        
        {showAtlasMap && (
          <Suspense fallback={null}>
            <AtlasMapSelector
              onClose={() => setShowAtlasMap(false)}
              onSelect={(selectedEntity) => {
                setShowAtlasMap(false);
                
                // If it's a country, navigate to the new SEO-friendly URL
                if (selectedEntity && selectedEntity.type === 'country' && selectedEntity.name) {
                  const countrySlug = createCountrySlug(selectedEntity.name);
                  navigate(`/countries/${countrySlug}`);
                } else {
                  // For non-countries or fallback, use the existing behavior
                  if (window.location.pathname !== '/') {
                    window.location.href = '/';
                    sessionStorage.setItem('selectedCountryFromAtlas', JSON.stringify({
                      ...selectedEntity,
                      showEntityInfo: true
                    }));
                  } else {
                    if (window.handleEntitySelectFromMain) {
                      window.handleEntitySelectFromMain(selectedEntity);
                    } else {
                      handleEntitySelection(selectedEntity);
                    }
                  }
                }
              }}
            />
          </Suspense>
        )}
      </header>
      
      {/* Mobile menu dropdown - positioned outside header */}
      <div className={`mobile-menu-dropdown ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          <Link
            to="/"
            className={`mobile-nav-link ${isActiveLink('/') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Entities
          </Link>
          <Link
            to="/trade"
            className={`mobile-nav-link ${isActiveLink('/trade') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Trade Data
          </Link>
          <Link
            to="/sanctions"
            className={`mobile-nav-link ${isActiveLink('/sanctions') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Sanctions
          </Link>
          <Link
            to="/feeds"
            className={`mobile-nav-link ${isActiveLink('/feeds') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Feeds
          </Link>
          <Link
            to="/news" 
            className={`mobile-nav-link ${isActiveLink('/news') ? 'active' : ''}`} 
            onClick={closeMobileMenu}
          >
            Articles
          </Link>
          <Link 
            to="/podcasts" 
            className={`mobile-nav-link ${isActiveLink('/podcasts') ? 'active' : ''}`} 
            onClick={closeMobileMenu}
          >
            Podcasts
          </Link>
          <Link 
            to="/streams" 
            className={`mobile-nav-link ${isActiveLink('/streams') ? 'active' : ''}`} 
            onClick={closeMobileMenu}
          >
            Streams
          </Link>
        </div>
      </div>
    </>
  );
}

export default Header;

